import type { BalanceData, AprData, PostTradeMetrics } from '../utils'
import type { LenderYields } from '../../../../types/lenderTypes'
import type { SumerPositionInput } from './types'
import { computePostTradeMetrics } from '../utils'
import {
  getOrganicYieldWithdraw,
  getRewardYieldWithdraw,
  getIntrinsicYieldWithdraw,
} from '../yield'
import {
  computeSumerWaterfall,
  buildSumerAccumulators,
  applyPositionDelta,
} from './waterfall'

/**
 * Compute post-trade balance metrics for a Sumer withdraw operation.
 *
 * Re-runs the waterfall absorption with decreased deposits for the target market.
 */
export function computeSumerWithdrawDelta(
  amount: number,
  price: number,
  targetMarketUid: string,
  balanceData: BalanceData,
  positions: SumerPositionInput[],
  apr?: AprData,
  yieldParams?: LenderYields,
): PostTradeMetrics {
  const dollarAmount = amount * price

  const updatedPositions = applyPositionDelta(
    positions,
    targetMarketUid,
    -dollarAmount,
    0,
    0,
  )

  const { groupRatesMap, gAccum, gAccumAll } =
    buildSumerAccumulators(updatedPositions)
  const groups = Object.values(groupRatesMap)
  const waterfallCollateral = computeSumerWaterfall(groups, gAccum)
  const waterfallCollateralAll = computeSumerWaterfall(groups, gAccumAll)

  const postTrade: BalanceData = {
    ...balanceData,
    borrowDiscountedCollateral: waterfallCollateral,
    borrowDiscountedCollateralAllActive: waterfallCollateralAll,
    collateral: waterfallCollateral,
    collateralAllActive: waterfallCollateralAll,
    deposits: balanceData.deposits - dollarAmount,
    debt: balanceData.debt,
    adjustedDebt: balanceData.debt,
    nav: balanceData.deposits - dollarAmount - balanceData.debt,
  }

  const result = computePostTradeMetrics(balanceData, postTrade)

  if (apr && yieldParams) {
    const cashFlowDeposits = balanceData.deposits * apr.depositApr
    const cashFlowDebt = balanceData.debt * apr.borrowApr
    const rewardFlowDeposits = balanceData.deposits * (apr.rewardDepositApr ?? 0)
    const rewardFlowDebt = balanceData.debt * (apr.rewardBorrowApr ?? 0)
    const intrinsicFlowDeposits = balanceData.deposits * apr.intrinsicDepositApr
    const intrinsicFlowDebt = balanceData.debt * apr.intrinsicBorrowApr

    const newApr = { ...apr }
    ;[newApr.borrowApr, newApr.depositApr, newApr.apr] =
      getOrganicYieldWithdraw(
        dollarAmount,
        cashFlowDeposits,
        cashFlowDebt,
        balanceData.deposits,
        balanceData.debt,
        yieldParams,
      )
    newApr.rewards = getRewardYieldWithdraw(
      dollarAmount,
      rewardFlowDeposits,
      rewardFlowDebt,
      balanceData.deposits,
      balanceData.debt,
      yieldParams,
    )
    ;[
      newApr.intrinsicBorrowApr,
      newApr.intrinsicDepositApr,
      newApr.intrinsicApr,
    ] = getIntrinsicYieldWithdraw(
      dollarAmount,
      intrinsicFlowDeposits,
      intrinsicFlowDebt,
      balanceData.deposits,
      balanceData.debt,
      yieldParams,
    )
    result.post.aprData = newApr
  }

  return result
}
